import * as ExcelJS from 'exceljs';

type WatermarkMeta = {
  visibleText?: string; // shown clearly in sheet
  printFooterText?: string; // shown lightly in header/footer (for PDF export)
};

const DEFAULT_VISIBLE_TEXT = '블루멘토 · 무료 플랜';
// Footer is intended to show mainly in print / PDF export.
const DEFAULT_FOOTER_TEXT = '블루멘토 제공 · 무료 플랜';

function colLetterToNumber(col: string) {
  // A -> 1, Z -> 26, AA -> 27
  let n = 0;
  for (const ch of col.toUpperCase()) {
    const code = ch.charCodeAt(0);
    if (code < 65 || code > 90) continue;
    n = n * 26 + (code - 64);
  }
  return n;
}

function parseA1(a1: string) {
  const m = /^([A-Za-z]+)(\d+)$/.exec(a1.trim());
  if (!m) return null;
  return { col: colLetterToNumber(m[1]), row: Number(m[2]) };
}

function parsePrintArea(printArea?: string) {
  if (!printArea) return null;
  // Excel may have multiple areas separated by comma; take first.
  const first = printArea.split(',')[0].trim();
  const parts = first.split(':');
  if (parts.length !== 2) return null;
  const start = parseA1(parts[0]);
  const end = parseA1(parts[1]);
  if (!start || !end) return null;
  return {
    startCol: Math.min(start.col, end.col),
    startRow: Math.min(start.row, end.row),
    endCol: Math.max(start.col, end.col),
    endRow: Math.max(start.row, end.row),
  };
}

function getWorksheetBounds(ws: ExcelJS.Worksheet) {
  // Prefer template print area (most reliable for "no extra page").
  const printBounds = parsePrintArea(ws.pageSetup?.printArea);
  if (printBounds) return printBounds;

  // Fallback: use existing cell dimensions (usually matches template content area).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dims = (ws as any).dimensions;
  if (dims && typeof dims === 'object') {
    const top = Number(dims.top) || 1;
    const left = Number(dims.left) || 1;
    const bottomFromDims = Number(dims.bottom) || 0;
    const rightFromDims = Number(dims.right) || 0;

    // actualRowCount/actualColumnCount reflect used cells; they are safer than hard-coded defaults.
    const bottomFromActual =
      ws.actualRowCount && ws.actualRowCount > 0 ? top + ws.actualRowCount - 1 : 0;
    const rightFromActual =
      ws.actualColumnCount && ws.actualColumnCount > 0 ? left + ws.actualColumnCount - 1 : 0;

    const bottom = Math.max(bottomFromDims, bottomFromActual, top);
    const right = Math.max(rightFromDims, rightFromActual, left);

    return { startCol: left, startRow: top, endCol: right, endRow: bottom };
  }

  // Safe fallback: keep watermark near top-left only (do NOT assume huge area).
  return {
    startCol: 1,
    startRow: 1,
    endCol: Math.max(1, ws.actualColumnCount || 1),
    endRow: Math.max(1, ws.actualRowCount || 1),
  };
}

function buildWatermarkPngBase64(text: string) {
  // Browser-only: exportWorkExcel runs client-side.
  if (typeof document === 'undefined') {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 600;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }

  // Transparent background
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Rotate and draw semi-transparent text
  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((-20 * Math.PI) / 180);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Big text
  ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
  ctx.font = '700 135px Arial, sans-serif';
  ctx.fillText(text, 0, 0);
  ctx.restore();

  return canvas.toDataURL('image/png');
}

async function protectWorksheetBestEffort(worksheet: ExcelJS.Worksheet) {
  // If we protect without unlocking cells, user can't edit.
  // We unlock only the used cells to keep normal editing possible.
  try {
    worksheet.eachRow({ includeEmpty: false }, (row) => {
      row.eachCell({ includeEmpty: false }, (cell) => {
        cell.protection = { locked: false };
      });
    });

    const password = `bm-${Math.random().toString(36).slice(2, 10)}`;
    // ExcelJS may ignore unknown options depending on version; keep minimal.
    // selectLockedCells=false reduces ability to click locked objects accidentally.
    // selectUnlockedCells=true allows normal editing.
    // objects=false intends to prevent editing shapes/images (if supported).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (worksheet as any).protect?.(password, {
      selectLockedCells: false,
      selectUnlockedCells: true,
      objects: false,
    });
  } catch {
    // best-effort only: do not block download
  }
}

export async function applyFreePlanWatermark(
  workbook: ExcelJS.Workbook,
  meta: WatermarkMeta = {}
) {
  const visibleText = meta.visibleText ?? DEFAULT_VISIBLE_TEXT;
  const footerText = meta.printFooterText ?? DEFAULT_FOOTER_TEXT;
  const watermarkBase64 = buildWatermarkPngBase64(visibleText);
  const watermarkImageId =
    watermarkBase64 != null
      ? workbook.addImage({ base64: watermarkBase64, extension: 'png' })
      : null;

  // Apply to all worksheets
  for (const ws of workbook.worksheets) {
    const originalPrintArea = ws.pageSetup?.printArea;
    const bounds = getWorksheetBounds(ws);

    // 1) Lightweight print/PDF watermark via footer (repeats per page)
    try {
      ws.headerFooter = {
        ...ws.headerFooter,
        oddFooter: `&C&8${footerText}`,
        evenFooter: `&C&8${footerText}`,
      };
    } catch {
      // ignore
    }

    // 2) Visible watermark overlay inside sheet (harder to miss)
    if (watermarkImageId != null) {
      // Keep watermark strictly inside the template bounds to avoid "extra pages".
      // Also repeat it down the sheet so multi-page exports always show it.
      const w = bounds.endCol - bounds.startCol + 1;
      const h = bounds.endRow - bounds.startRow + 1;

      // Safety margins so the image won't "hang" past the print area edge.
      // const marginCols = Math.max(1, Math.floor(w * 0.08));
      // const marginRows = Math.max(1, Math.floor(h * 0.05));
      // const safeEndCol = bounds.endCol - marginCols;
      // const safeStartRow = bounds.startRow + marginRows;
      // const safeEndRow = bounds.endRow - marginRows;
      const safeEndCol = bounds.endCol;
      const safeStartRow = bounds.startRow;
      const safeEndRow = bounds.endRow;

      // Smaller image reduces chance of changing page layout.
      // const imageExt = { width: 420, height: 210 };

      const imageExt = { width: 600, height: 300 };


      // Dense watermark spacing: Use smaller step (10 rows) to compensate for variable row heights.
      const pageStepRows = 10;

      // Calculate number of device sections (each device = 12 columns: A-L, M-X, Y-...)
      // Template layout: each device occupies 12 columns starting from A (col 1), M (col 13), Y (col 25), etc.
      const columnsPerDevice = 12;
      
      // Always calculate from column A (col 1), regardless of bounds.startCol
      // This ensures watermark always starts from column A for first section
      const numDevices = Math.ceil((safeEndCol - 1 + 1) / columnsPerDevice);

      // Place watermarks at the START of each device section (column A, M, Y, ...)
      // This ensures watermark appears in all pages (left, middle, right)
      const watermarkCols: number[] = [];
      
      // Each section starts at: col 1 (A), col 13 (M), col 25 (Y), etc.
      // Watermark position: col 0 (A), col 12 (M), col 24 (Y), etc.
      // Use 0 (not 0.5) to ensure watermark starts from the beginning of column A
      for (let deviceIndex = 0; deviceIndex < numDevices; deviceIndex++) {
        const sectionStartCol = 1 + deviceIndex * columnsPerDevice; // col 1, 13, 25, ...
        // Position at the start of each section (col 0 relative to section start = col - 1)
        const watermarkCol = sectionStartCol - 1; // col 0, 12, 24, etc.
        if (watermarkCol <= safeEndCol && watermarkCol >= 0) {
          watermarkCols.push(watermarkCol);
        }
      }

      // Consistent spacing: Start from safeStartRow with fixed pageStepRows increment.
      const firstWatermarkRow = safeStartRow;
      const lastWatermarkRow = safeEndRow - 10; // Leave margin at bottom

      // Place watermarks: for each column position, repeat down the sheet
      for (const watermarkCol of watermarkCols) {
        for (
          let row = firstWatermarkRow;
          row <= lastWatermarkRow;
          row += pageStepRows
        ) {
          const clamped = {
            col: watermarkCol, // Use exact column position
            row: Math.max(safeStartRow, Math.min(row, safeEndRow)),
          };

          try {
            ws.addImage(watermarkImageId, {
              tl: { col: clamped.col, row: clamped.row },
              ext: imageExt,
              editAs: 'oneCell',
            });
          } catch {
            // ignore
          }
        }
      }
    } else {
      // Fallback (no canvas): put a big light text in a merged cell.
      try {
        const cell = ws.getCell('A1');
        cell.value = visibleText;
        cell.font = { bold: true, size: 28, color: { argb: '33AAAAAA' } };
      } catch {
        // ignore
      }
    }

    // Re-apply print settings after adding drawings
    // IMPORTANT: Keep original page setup (scale: 100) to preserve page breaks
    // Do NOT use fitToHeight = 0 as it allows compressing multiple pages into one
    try {
      if (!ws.pageSetup) ws.pageSetup = {};
      
      if (originalPrintArea) {
        ws.pageSetup.printArea = originalPrintArea;
      }
      
      // Keep original page setup from template (paperSize: 9, scale: 100)
      // This preserves page breaks that were set before watermark was applied
      // Only set paperSize if not already set, but preserve scale
      if (!ws.pageSetup.paperSize) {
        ws.pageSetup.paperSize = 9; // A4
      }
      
      // DO NOT use fitToPage/fitToHeight = 0 as it breaks page breaks
      // Template uses scale: 100, so we keep that approach
      // If template had fitToPage, it would have been set already
      
      // Remove fitToPage settings if they were added (they break page breaks)
      if (ws.pageSetup.fitToPage !== undefined) {
        // Only keep fitToPage if template originally had it
        // But since templates use scale: 100, we should not use fitToPage
        delete (ws.pageSetup as any).fitToPage;
        delete (ws.pageSetup as any).fitToWidth;
        delete (ws.pageSetup as any).fitToHeight;
      }
      
      // Ensure scale is set (templates use 100)
      if (!ws.pageSetup.scale) {
        ws.pageSetup.scale = 100;
      }
    } catch {
      // ignore
    }

    // 3) Best-effort protection to make removal harder (without breaking editing)
    await protectWorksheetBestEffort(ws);
  }
}

